import {
    ArrowRight, Bot, Calendar, ClipboardCheck, Phone, Shield,
    Stethoscope, UtensilsCrossed, Home as HomeIcon, Dumbbell,
    Users, Clock, MessageSquare
} from 'lucide-react';

const PORTAL_URL = import.meta.env.VITE_PORTAL_URL || 'http://localhost:3200';
interface UseCase {
    icon: typeof Phone;
    title: string;
    description: string;
}

interface Industry {
    id: string;
    icon: typeof Stethoscope;
    name: string;
    tagline: string;
    gradientClass: string;
    useCases: UseCase[];
    stats: { value: string; label: string }[];
}

const INDUSTRIES: Industry[] = [
    {
        id: 'healthcare',
        icon: Stethoscope,
        name: 'Healthcare',
        tagline: 'Patient communication that never sleeps',
        gradientClass: 'grad-nova-plasma',
        useCases: [
            { icon: Calendar, title: 'Appointment Scheduling', description: 'Patients call in, your agent checks availability and books — instantly. Integrates with Noterro, Jane App, and custom EHRs.' },
            { icon: Phone, title: 'Confirmation Calls', description: 'Automated outbound calls to confirm appointments 24h in advance. Reduce no-shows by 60%.' },
            { icon: ClipboardCheck, title: 'Patient Intake', description: 'Collect pre-visit information, insurance details, and medical history over the phone — securely.' },
            { icon: MessageSquare, title: 'After-Hours Support', description: 'Your agent handles calls 24/7, triages urgency, and escalates emergencies to on-call staff.' },
        ],
        stats: [
            { value: '60%', label: 'No-show reduction' },
            { value: '3min', label: 'Avg booking time' },
            { value: '24/7', label: 'Availability' },
        ],
    },
    {
        id: 'restaurants',
        icon: UtensilsCrossed,
        name: 'Restaurants',
        tagline: 'Never miss a reservation again',
        gradientClass: 'grad-solar-ember',
        useCases: [
            { icon: Calendar, title: 'Reservation Management', description: 'Handle bookings, modifications, and cancellations over the phone. Syncs with OpenTable and Resy.' },
            { icon: Phone, title: 'Order Taking', description: 'Take phone orders for pickup and delivery. Upsell specials and collect payment details securely.' },
            { icon: Users, title: 'Event Bookings', description: 'Manage large party reservations, private events, and catering inquiries with multi-step workflows.' },
            { icon: Clock, title: 'Wait Time Quotes', description: 'Real-time wait time estimates and callback notifications when tables are ready.' },
        ],
        stats: [
            { value: '85%', label: 'Calls answered' },
            { value: '$2.4K', label: 'Monthly savings' },
            { value: '12s', label: 'Avg answer time' },
        ],
    },
    {
        id: 'mortgage',
        icon: HomeIcon,
        name: 'Mortgage & Finance',
        tagline: 'Qualify leads while you sleep',
        gradientClass: 'grad-plasma',
        useCases: [
            { icon: Bot, title: 'Lead Qualification', description: 'Screen inbound leads with qualifying questions — credit score, down payment, property type — before routing to brokers.' },
            { icon: Phone, title: 'Rate Callbacks', description: 'Outbound calls to warm leads with personalized rate quotes and pre-approval information.' },
            { icon: ClipboardCheck, title: 'Application Follow-up', description: 'Automated check-ins on incomplete applications. Collect missing documents and answer common questions.' },
            { icon: Shield, title: 'Compliance Calls', description: 'Disclosure readings, consent collection, and regulatory callbacks — fully audited and recorded.' },
        ],
        stats: [
            { value: '3x', label: 'Lead conversion' },
            { value: '70%', label: 'Qualification rate' },
            { value: '$180', label: 'Cost per lead saved' },
        ],
    },
    {
        id: 'athlete',
        icon: Dumbbell,
        name: 'Athlete Management',
        tagline: 'Keep your roster connected at scale',
        gradientClass: 'grad-nova-glow',
        useCases: [
            { icon: Calendar, title: 'Training Reminders', description: 'Automated calls to athletes for training schedules, session changes, and facility updates.' },
            { icon: Users, title: 'Recruitment Outreach', description: 'Personalized calls to prospects with program details, scholarship info, and campus visit scheduling.' },
            { icon: MessageSquare, title: 'Fan Engagement', description: 'Post-game updates, ticket promotions, and event reminders via voice for premium fan experiences.' },
            { icon: ClipboardCheck, title: 'Wellness Check-ins', description: 'Regular check-ins for injury recovery status, mental health support, and return-to-play clearance.' },
        ],
        stats: [
            { value: '92%', label: 'Contact rate' },
            { value: '40%', label: 'Admin time saved' },
            { value: '500+', label: 'Calls/week' },
        ],
    },
];

export default function Industries() {
    return (
        <div style={{ paddingTop: '7rem', paddingBottom: '4rem' }}>
            {/* Header */}
            <section className="section-container text-center" style={{ marginBottom: '4rem' }}>
                <h1
                    className="font-display font-extrabold text-starlight"
                    style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '1rem', lineHeight: 1.15 }}
                >
                    Voice AI for{' '}
                    <span className="gradient-text">your industry</span>
                </h1>
                <p className="text-stardust" style={{ fontSize: '1.125rem', opacity: 0.7, maxWidth: '640px', margin: '0 auto' }}>
                    Pre-built workflows, compliance rules, and integrations for the
                    industries that benefit most from autonomous voice agents.
                </p>
            </section>

            {/* Industry Sections */}
            {INDUSTRIES.map((industry, idx) => (
                <section
                    key={industry.id}
                    id={industry.id}
                    className={idx % 2 === 1 ? 'mesh-gradient' : ''}
                    style={{ padding: '5rem 0' }}
                >
                    <div className="section-container">
                        {/* Header */}
                        <div className="flex items-start" style={{ gap: '1.25rem', marginBottom: '2.5rem' }}>
                            <div
                                className={`icon-box-lg ${industry.gradientClass}`}
                                style={{ flexShrink: 0 }}
                            >
                                <industry.icon style={{ width: 28, height: 28, color: 'white' }} />
                            </div>
                            <div>
                                <h2
                                    className="font-display font-bold text-starlight"
                                    style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', marginBottom: '0.25rem' }}
                                >
                                    {industry.name}
                                </h2>
                                <p className="text-stardust" style={{ opacity: 0.7 }}>
                                    {industry.tagline}
                                </p>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-3" style={{ gap: '1rem', marginBottom: '2.5rem' }}>
                            {industry.stats.map((stat, i) => (
                                <div key={i} className="glass text-center" style={{ borderRadius: '14px', padding: '1rem' }}>
                                    <p className="font-display font-bold gradient-text" style={{ fontSize: '1.5rem' }}>
                                        {stat.value}
                                    </p>
                                    <p className="text-stardust" style={{ fontSize: '0.7rem', opacity: 0.5, marginTop: '0.25rem' }}>
                                        {stat.label}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Use Cases */}
                        <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: '1.25rem' }}>
                            {industry.useCases.map((uc, i) => (
                                <div
                                    key={i}
                                    className="glass glass-hover group animate-fade-in-up"
                                    style={{
                                        borderRadius: '16px',
                                        padding: '1.5rem',
                                        display: 'flex',
                                        gap: '1rem',
                                        animationDelay: `${i * 100}ms`,
                                        transition: 'transform 300ms',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: 12,
                                            backgroundColor: 'var(--color-cosmos)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            transition: 'background-color 300ms',
                                        }}
                                    >
                                        <uc.icon style={{ width: 20, height: 20, color: 'var(--color-nova)' }} />
                                    </div>
                                    <div>
                                        <h3 className="font-display font-semibold text-starlight" style={{ marginBottom: '0.25rem' }}>
                                            {uc.title}
                                        </h3>
                                        <p className="text-stardust" style={{ fontSize: '0.8125rem', lineHeight: 1.6, opacity: 0.6 }}>
                                            {uc.description}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            ))}

            {/* Bottom CTA */}
            <section className="relative overflow-hidden" style={{ padding: '5rem 0' }}>
                <div className="aurora aurora-1" style={{ opacity: 0.15 }} />
                <div className="section-container text-center relative z-10" style={{ maxWidth: '800px' }}>
                    <h2
                        className="font-display font-bold text-starlight"
                        style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', marginBottom: '1rem' }}
                    >
                        Don't see your industry?
                    </h2>
                    <p className="text-stardust" style={{ opacity: 0.7, marginBottom: '2rem' }}>
                        Lumina's flexible platform works for any business that uses phones.
                        Talk to us about your custom use case.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <a href={`${PORTAL_URL}/signup`} className="btn-primary glow-cta" style={{ padding: '0.875rem 2rem' }}>
                            Start Free Trial
                            <ArrowRight style={{ width: 16, height: 16 }} />
                        </a>
                        <a href="/" className="btn-secondary" style={{ padding: '0.875rem 2rem' }}>
                            <Phone style={{ width: 16, height: 16 }} />
                            Talk to Luna
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
